import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { TableCell, TableRow } from "@/components/ui/table";
import { PropFirmWithPrograms } from '@/types';
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck, AlertTriangle, CircleDashed } from 'lucide-react';
import DetailTooltip from './DetailTooltip';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { ErrorBoundary } from '@/components/ui/error-boundary';
import { FirmAvatar } from '@/components/ui/FirmAvatar';
import { useAffiliateClick } from '@/hooks/useAffiliateClick';
import { getVerificationState, getVerificationLabel, getVerificationColor, formatVerifiedDate, getCtaLabel, hasVerifiedDiscount } from '@/utils/verification';

interface FirmTableRowProps {
  firm: PropFirmWithPrograms;
  selectedAccountSize: string;
  index: number;
}

function getDrawdownColor(_drawdownType: string): string {
  return 'text-amber-600 dark:text-amber-400';
}

function FirmRowContent({ firm, selectedAccountSize, index }: FirmTableRowProps) {
  const [copied, setCopied] = useState(false);
  const { trackClick } = useAffiliateClick();
  const accountSizeNum = parseInt(selectedAccountSize.replace('k', '000'));
  const program = firm.funding_programs?.find(p => p?.account_size === accountSizeNum);

  if (!program) {
    return null;
  }

  const effectivePrice = program.discounted_price ?? program.base_price;
  const hasDiscount = program.discounted_price != null && program.base_price != null && program.discounted_price < program.base_price;
  const targetUrl = firm.affiliate_url || firm.website_url || '#';

  const handleCopyCode = () => {
    if (firm.coupon_code) {
      navigator.clipboard.writeText(firm.coupon_code).catch(() => {});
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'coupon_copy', {
          firm_name: firm.name,
          coupon_code: firm.coupon_code,
        });
      }
    }
  };

  const handleJoinNowClick = () => {
    // Let the <a> navigate naturally — don't preventDefault, don't window.open
    trackClick({
      firmName: firm.name,
      accountSize: selectedAccountSize,
      discountCode: firm.coupon_code || null,
      affiliateUrl: targetUrl,
      source: 'comparison_table',
      skipNavigation: true,
    });
  };

  const profitTargetAmt = program.profit_target ? `$${program.profit_target.toLocaleString()}` : '—';
  const maxDrawdownAmt = program.max_drawdown ? `$${program.max_drawdown.toLocaleString()}` : '—';
  const drawdownColor = getDrawdownColor(program.drawdown_type || '');

  return (
    <TableRow className={index % 2 === 0 ? 'bg-white dark:bg-gray-900/50' : 'bg-gray-50/50 dark:bg-gray-800/50'}>
      {/* Firm name + verification indicator */}
      <TableCell className="py-4">
        <div className="flex items-center gap-3">
          <FirmAvatar slug={firm.slug} name={firm.name} logoUrl={firm.logo_url} size="w-10 h-10" />
          <div className="min-w-0">
            <Link
              to={`/firms/${firm.slug}`}
              className="font-medium text-gray-900 dark:text-white hover:text-brand-blue dark:hover:text-blue-400 transition-colors block"
            >
              {firm.name}
            </Link>
            {(() => {
              const vState = getVerificationState(firm.last_verified_at);
              const vColor = getVerificationColor(vState);
              const Icon = vState === 'verified' ? ShieldCheck : vState === 'stale' ? AlertTriangle : CircleDashed;
              return (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className={`inline-flex items-center gap-1 text-[10px] mt-0.5 cursor-help ${vColor.text}`}>
                        <Icon className="h-2.5 w-2.5" />
                        {getVerificationLabel(vState)}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      <p className="text-xs">{formatVerifiedDate(firm.last_verified_at)}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              );
            })()}
          </div>
        </div>
      </TableCell>

      {/* Coupon code */}
      <TableCell>
        {firm.coupon_code ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleCopyCode}
                  className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded text-sm font-mono dark:bg-emerald-900/30 dark:text-emerald-300 hover:bg-emerald-200 dark:hover:bg-emerald-900/50 transition-colors cursor-pointer"
                >
                  {copied ? 'Copied!' : firm.coupon_code}
                </button>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p>{copied ? '✓ Copied!' : 'Click to copy code'}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <span className="text-gray-400 text-sm dark:text-gray-500">—</span>
        )}
      </TableCell>

      {/* Price */}
      <TableCell>
        <div>
          {hasDiscount && program.base_price != null && (
            <div className="text-xs text-gray-400 line-through dark:text-gray-500 leading-none mb-0.5">
              ${program.base_price.toFixed(2)}
            </div>
          )}
          <div className={`font-semibold ${hasDiscount ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-900 dark:text-white'}`}>
            {effectivePrice != null ? `$${effectivePrice.toFixed(2)}` : '—'}
          </div>
        </div>
      </TableCell>

      {/* Profit target / Max drawdown */}
      <TableCell className="w-32">
        <div className="text-center">
          <div className="font-medium text-emerald-500 dark:text-emerald-400">{profitTargetAmt}</div>
          <div className="text-red-500 dark:text-red-400">{maxDrawdownAmt}</div>
        </div>
      </TableCell>

      {/* Drawdown type */}
      <TableCell className="w-44">
        <DetailTooltip
          title="Drawdown Type"
          content={
            <div>
              <p>{program.drawdown_type}</p>
              {program.daily_loss_limit != null && (
                <p className="mt-1">Daily limit: ${program.daily_loss_limit.toLocaleString()}</p>
              )}
            </div>
          }
          side="top"
          align="center"
        >
          <div className={`font-medium ${drawdownColor}`}>
            {(program.drawdown_type || 'Standard').replace(' Drawdown', '')}
          </div>
        </DetailTooltip>
      </TableCell>

      {/* Min trading days */}
      <TableCell>
        <div className="text-gray-900 dark:text-gray-100 text-center text-sm">
          {firm.min_trading_days || 'None'}
        </div>
      </TableCell>

      {/* Profit split */}
      <TableCell>
        <div className="text-gray-900 dark:text-gray-100">
          {firm.profit_split || '—'}
        </div>
      </TableCell>

      {/* CTA */}
      <TableCell className="text-center">
        <Button
          asChild
          size="lg"
          className="bg-orange-500 hover:bg-orange-600 text-white font-semibold shadow-md shadow-orange-500/20 hover:shadow-lg hover:shadow-orange-500/30 transition-all duration-200 w-[130px]"
        >
          <a
            href={targetUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleJoinNowClick}
          >
            {getCtaLabel(!!firm.affiliate_url, hasVerifiedDiscount(program.base_price, program.discounted_price, firm.last_verified_at))}
            <ArrowRight className="ml-2 h-4 w-4" />
          </a>
        </Button>
      </TableCell>
    </TableRow>
  );
}

export function FirmTableRow(props: FirmTableRowProps) {
  return (
    <ErrorBoundary
      fallback={
        <TableRow>
          <TableCell colSpan={8} className="text-center py-4 text-red-600">
            Error displaying this firm's data. Please try refreshing the page.
          </TableCell>
        </TableRow>
      }
    >
      <FirmRowContent {...props} />
    </ErrorBoundary>
  );
}
